import { FBMessage } from '@/types/fb-type';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const BOTTOM_THRESHOLD = 80;
const TOP_THRESHOLD = 80;

type UseChatWindowScrollParams = {
  messages: FBMessage[];
  isLoadingMessage: boolean;
  isLoadingMore: boolean;
  prevCursor: string | null;
  onLoadMoreMessage?: (after: string) => void;
};

export function useChatWindowScroll({
  messages,
  isLoadingMessage,
  isLoadingMore,
  prevCursor,
  onLoadMoreMessage,
}: UseChatWindowScrollParams) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isPinnedRef = useRef(true);
  const [showScrollChip, setShowScrollChip] = useState(false);
  const [unseenCount, setUnseenCount] = useState(0);

  const lockRef = useRef(false);
  const prevCursorRef = useRef<string | null>(null);
  const onLoadMoreRef = useRef<((c: string) => void) | null>(null);
  const lastRequestedCursorRef = useRef<string | null>(null);
  const pendingHistoryLoadRef = useRef(false);
  const savedScrollHeightRef = useRef(0);
  const savedScrollTopRef = useRef(0);
  const didInitialScrollRef = useRef(false);
  const prevMsgCountRef = useRef(0);

  useEffect(() => {
    prevCursorRef.current = prevCursor;
  }, [prevCursor]);

  useEffect(() => {
    onLoadMoreRef.current = onLoadMoreMessage ?? null;
  }, [onLoadMoreMessage]);

  const scrollToBottom = useCallback((smooth: boolean) => {
    const el = scrollRef.current;
    if (!el) return;
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'end' });
    }
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  }, []);

  useLayoutEffect(() => {
    if (!isLoadingMessage && messages.length > 0 && !didInitialScrollRef.current) {
      requestAnimationFrame(() => {
        scrollToBottom(false);
        requestAnimationFrame(() => scrollToBottom(false));
      });
      didInitialScrollRef.current = true;
      isPinnedRef.current = true;
      prevMsgCountRef.current = messages.length;
    }
  }, [isLoadingMessage, messages.length, scrollToBottom]);

  useEffect(() => {
    const prev = prevMsgCountRef.current;
    const curr = messages.length;
    if (curr <= prev || !didInitialScrollRef.current) {
      prevMsgCountRef.current = curr;
      return;
    }

    if (isPinnedRef.current) {
      requestAnimationFrame(() => scrollToBottom(true));
    } else {
      setUnseenCount((count) => count + (curr - prev));
      setShowScrollChip(true);
    }
    prevMsgCountRef.current = curr;
  }, [messages.length, scrollToBottom]);

  useEffect(() => {
    if (!isLoadingMore) {
      lockRef.current = false;
    }

    const requestedCursor = lastRequestedCursorRef.current;
    if (requestedCursor && (!prevCursor || prevCursor !== requestedCursor)) {
      lastRequestedCursorRef.current = null;
    }
  }, [isLoadingMore, prevCursor]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || !pendingHistoryLoadRef.current || isLoadingMore) return;
    if (savedScrollHeightRef.current <= 0) return;

    if (el.scrollHeight > savedScrollHeightRef.current) {
      const delta = el.scrollHeight - savedScrollHeightRef.current;
      el.scrollTop = savedScrollTopRef.current + delta;
    }

    savedScrollHeightRef.current = 0;
    savedScrollTopRef.current = 0;
    pendingHistoryLoadRef.current = false;
  }, [messages.length, isLoadingMore]);

  useEffect(() => {
    if (isLoadingMore || !pendingHistoryLoadRef.current) return;
    if (savedScrollHeightRef.current <= 0) {
      pendingHistoryLoadRef.current = false;
      return;
    }

    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollHeight <= savedScrollHeightRef.current) {
      savedScrollHeightRef.current = 0;
      savedScrollTopRef.current = 0;
      pendingHistoryLoadRef.current = false;
    }
  }, [isLoadingMore]);

  const onDivScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const pinned = el.scrollHeight - (el.scrollTop + el.clientHeight) < BOTTOM_THRESHOLD;
    isPinnedRef.current = pinned;
    if (pinned) {
      setShowScrollChip(false);
      setUnseenCount(0);
    }

    if (
      el.scrollTop <= TOP_THRESHOLD
      && !lockRef.current
      && !isLoadingMore
      && onLoadMoreRef.current
      && prevCursorRef.current
    ) {
      const nextCursor = prevCursorRef.current;
      if (lastRequestedCursorRef.current === nextCursor) return;
      lockRef.current = true;
      lastRequestedCursorRef.current = nextCursor;
      pendingHistoryLoadRef.current = true;
      savedScrollHeightRef.current = el.scrollHeight;
      savedScrollTopRef.current = el.scrollTop;
      onLoadMoreRef.current(nextCursor);
    }
  }, [isLoadingMore]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', onDivScroll, { passive: true });
    return () => el.removeEventListener('scroll', onDivScroll);
  }, [onDivScroll]);

  const handleScrollChipClick = () => {
    isPinnedRef.current = true;
    setShowScrollChip(false);
    setUnseenCount(0);
    scrollToBottom(true);
  };

  return {
    scrollRef,
    messagesEndRef,
    showScrollChip,
    unseenCount,
    scrollToBottom,
    handleScrollChipClick,
    setPinnedToBottom: () => {
      isPinnedRef.current = true;
    },
  };
}
